import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import type { CurrentUserResponse } from "@multi-chat/shared";
import { isApiError, isBackendUnavailable, isUnauthenticated } from "../../api/client.js";
import { getCurrentUser, getSession, logout as logoutApi } from "../../api/auth-api.js";
import { clearFreshAuthentication, clearWorkspaceEntered, markFreshAuthentication } from "../organizations/workspace-entry.js";
import { applyMemberDisplayNameToUser } from "./apply-member-display-name.js";
import { applyOrganizationNameToUser } from "./apply-organization-name.js";

export type AuthPhase =
  | { kind: "loading" }
  | { kind: "unauthenticated" }
  | { kind: "authenticating" }
  | { kind: "authenticated"; user: CurrentUserResponse }
  | { kind: "error"; message: string; backendUnavailable: boolean };

interface AuthContextValue {
  phase: AuthPhase;
  /** Re-run auth bootstrap (after Google popup/redirect or on retry). */
  retry: () => void;
  /** @deprecated Prefer `retry`; kept as a readable alias after successful OAuth. */
  refreshAfterAuth: () => void;
  logout: () => Promise<void>;
  setAuthenticating: () => void;
  /** Patch one organization name in the current session without a full bootstrap. */
  applyOrganizationName: (organizationId: number, name: string) => void;
  /** Patch the current member's organization-scoped name without a full bootstrap. */
  applyMemberDisplayName: (organizationId: number, displayName: string) => void;
  /** Replace the authenticated user after invitation acceptance. */
  adoptSessionUser: (user: CurrentUserResponse) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<AuthPhase>({ kind: "loading" });
  const [retryToken, setRetryToken] = useState(0);

  // An Effect Event, so a language change (new `t`) never re-runs the session bootstrap.
  const bootstrap = useEffectEvent(
    async (signal: AbortSignal) => {
      setPhase({ kind: "loading" });
      try {
        const session = await getSession(signal);
        if (signal.aborted) return;
        if (!session.authenticated) {
          setPhase({ kind: "unauthenticated" });
          return;
        }
        const user = await getCurrentUser(signal);
        if (signal.aborted) return;
        setPhase({ kind: "authenticated", user });
      } catch (error) {
        if (signal.aborted) return;
        if (isApiError(error) && isUnauthenticated(error)) {
          setPhase({ kind: "unauthenticated" });
          return;
        }
        const backendUnavailable = isBackendUnavailable(error);
        const message = backendUnavailable
          ? t("auth.backendUnavailable")
          : isApiError(error)
            ? error.message
            : t("auth.unexpectedError");
        setPhase({ kind: "error", message, backendUnavailable });
      }
    },
  );

  useEffect(() => {
    const controller = new AbortController();
    void bootstrap(controller.signal);
    return () => controller.abort();
  }, [retryToken]);

  const retry = useCallback(() => {
    setRetryToken((token) => token + 1);
  }, []);

  const setAuthenticating = useCallback(() => {
    markFreshAuthentication();
    setPhase({ kind: "authenticating" });
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // Even if logout fails, clear local state and return to login.
    }
    clearWorkspaceEntered();
    clearFreshAuthentication();
    setPhase({ kind: "unauthenticated" });
  }, []);

  const applyOrganizationName = useCallback((organizationId: number, name: string) => {
    setPhase((current) => {
      if (current.kind !== "authenticated") return current;
      return {
        kind: "authenticated",
        user: applyOrganizationNameToUser(current.user, organizationId, name),
      };
    });
  }, []);

  const applyMemberDisplayName = useCallback((organizationId: number, displayName: string) => {
    setPhase((current) => {
      if (current.kind !== "authenticated") return current;
      return {
        kind: "authenticated",
        user: applyMemberDisplayNameToUser(current.user, organizationId, displayName),
      };
    });
  }, []);

  const adoptSessionUser = useCallback((user: CurrentUserResponse) => {
    setPhase({ kind: "authenticated", user });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      phase,
      retry,
      refreshAfterAuth: retry,
      logout,
      setAuthenticating,
      applyOrganizationName,
      applyMemberDisplayName,
      adoptSessionUser,
    }),
    [
      phase,
      retry,
      logout,
      setAuthenticating,
      applyOrganizationName,
      applyMemberDisplayName,
      adoptSessionUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
